import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { ClientRow, errorMessage, operatorApi, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, Empty, ErrorBox, Field, Modal, PageHeader, SecretBox, Spinner, Table, Td, ago, inputClass, num } from '../../components/ui';

export function BalanceBadge({ client }: { client: ClientRow }) {
  if (client.billing_mode === 'free') return <Badge>Accès libre</Badge>;
  const tone = client.balance < 0 ? 'red' : client.available <= client.low_credit_threshold ? 'amber' : 'green';
  return <Badge tone={tone}>{num(client.available)} dispo.</Badge>;
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const { data, error, loading, reload } = useFetch(
    async () => (await operatorApi.get<{ clients: ClientRow[] }>('/clients', { params: { search: search || undefined } })).data.clients,
    [search]
  );

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle="Les organisations qui utilisent le service, leur solde de signatures et leur activité"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4" />
            Nouveau client
          </Button>
        }
      />

      <Card>
        <div className="relative mb-4 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input className={`${inputClass} pl-9`} placeholder="Rechercher un client…" value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>

        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorBox message={error} onRetry={reload} />
        ) : !data || data.length === 0 ? (
          <Empty>{search ? 'Aucun client ne correspond.' : 'Aucun client. Créez le premier avec « Nouveau client ».'}</Empty>
        ) : (
          <Table head={['Client', 'Statut', 'Solde', 'Signatures (30 j)', 'Total', 'Dernier document', 'Clés']}>
            {data.map((client) => (
              <tr key={client.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/clients/${client.id}`)}>
                <Td>
                  <Link to={`/clients/${client.id}`} className="font-medium text-slate-900 hover:text-indigo-600" onClick={(event) => event.stopPropagation()}>
                    {client.name}
                  </Link>
                  <div className="text-xs text-slate-500">{client.email}</div>
                </Td>
                <Td>{client.is_active ? <Badge tone="green">Actif</Badge> : <Badge tone="red">Suspendu</Badge>}</Td>
                <Td><BalanceBadge client={client} /></Td>
                <Td className="tabular-nums">{num(client.signatures_30d)}</Td>
                <Td className="tabular-nums">{num(client.signatures_total)}</Td>
                <Td className="text-slate-500">{ago(client.last_document_at)}</Td>
                <Td className="tabular-nums">{client.active_keys}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {creating && (
        <CreateClientModal
          onClose={() => setCreating(false)}
          onCreated={() => void reload()}
        />
      )}
    </div>
  );
}

function CreateClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const settings = useFetch(async () => (await operatorApi.get<{ tariff: { trial_credits: number } }>('/settings')).data);
  const [form, setForm] = useState({ name: '', email: '', contact_name: '', phone: '', billing_mode: 'prepaid', trial_credits: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ id: string; trial_credits: number; api_key: { key: string; prefix: string } | null } | null>(null);
  const navigate = useNavigate();

  const set = (field: string, value: string) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const response = await operatorApi.post('/clients', {
        name: form.name,
        email: form.email,
        contact_name: form.contact_name || undefined,
        phone: form.phone || undefined,
        billing_mode: form.billing_mode,
        trial_credits: form.trial_credits === '' ? undefined : Number(form.trial_credits),
      });
      setResult(response.data);
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <Modal title="Client créé" onClose={onClose}>
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            Le client est prêt{result.trial_credits > 0 ? ` avec ${result.trial_credits} signature(s) offerte(s)` : ''}. Transmettez-lui sa clé API avec le guide client.
          </p>
          {result.api_key && <SecretBox label={`Clé API ${result.api_key.prefix}…`} value={result.api_key.key} />}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Fermer</Button>
            <Button onClick={() => navigate(`/clients/${result.id}`)}>Ouvrir la fiche</Button>
          </div>
        </div>
      </Modal>
    );
  }

  const defaultTrial = settings.data?.tariff.trial_credits ?? 0;

  return (
    <Modal title="Nouveau client" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nom de l'organisation">
          <input className={inputClass} value={form.name} onChange={(e) => set('name', e.target.value)} required minLength={2} autoFocus />
        </Field>
        <Field label="Adresse e-mail" hint="Identifie le client ; une adresse ne peut servir qu'à un seul client.">
          <input className={inputClass} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Contact (facultatif)">
            <input className={inputClass} value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} />
          </Field>
          <Field label="Téléphone (facultatif)">
            <input className={inputClass} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Mode de facturation" hint={form.billing_mode === 'prepaid' ? 'Un crédit par signature, refus si le solde est insuffisant.' : 'Aucun contrôle ni débit.'}>
            <select className={inputClass} value={form.billing_mode} onChange={(e) => set('billing_mode', e.target.value)}>
              <option value="prepaid">Lots prépayés</option>
              <option value="free">Accès libre</option>
            </select>
          </Field>
          {form.billing_mode === 'prepaid' && (
            <Field label="Signatures offertes" hint="Essai gratuit à l'ouverture du compte.">
              <input className={inputClass} type="number" min={0} placeholder={String(defaultTrial)} value={form.trial_credits} onChange={(e) => set('trial_credits', e.target.value)} />
            </Field>
          )}
        </div>
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={saving}>Créer le client et sa clé</Button>
        </div>
      </form>
    </Modal>
  );
}
